// @flow

import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  SURVEY_NUTRIENT_LAYERS,
  SURVEY_TEXTURE_LAYERS,
  SURVEY_TOPOGRAPHY_LAYERS,
} from 'js/constants/SurveyLayers';
import { useLangFile, useLangFileRef } from 'js/context/LanguageContext';
import List from '@mui/material/List';
import { voidFunc } from '../../constants/PropTypeUtils';
import SubList from '../SatelliteLayerList/SubList';
import { useCanLayerBeEnabled } from '../../context/SurveyContext';
import ViewModeConstants from '../../constants/ViewModeConstants';
import { getLayerConfig } from '../Prescription/PrescriptionUtils';
import { Box, ListItemSecondaryAction, Paper } from '@mui/material';
import { DefaultColoringStrategy } from '../../model/surveys/SurveyColoringStrategies';
import MapLegendLayerListItem from '../MapLegend/MapLegendLayerListItem';
import { grey } from '@mui/material/colors';
import { isSurveyLayerEnabledInCountry } from '../../model/surveys/country/SurveyCountryStrategy';
import { useSurveyCountryCode } from '../../hooks/useSurveyCountryCode';

const SurveyLayerList = ({
  selectedLayer,
  onLayerChanged,
  onReferencesChanged,
  isSingleField,
  enableMultipleSelect,
  multipleSelections,
  disableAll,
  viewMode,
}) => {
  const LangFile = useLangFile();
  const LangFileRef = useLangFileRef();
  const countryCode = useSurveyCountryCode();

  const nutrientLayers = useMemo(
    () =>
      SURVEY_NUTRIENT_LAYERS.filter((layer) => isSurveyLayerEnabledInCountry(layer, countryCode)),
    [countryCode]
  );
  const textureLayers = useMemo(
    () =>
      SURVEY_TEXTURE_LAYERS.filter((layer) => isSurveyLayerEnabledInCountry(layer, countryCode)),
    [countryCode]
  );
  const topographyLayers = useMemo(
    () =>
      SURVEY_TOPOGRAPHY_LAYERS.filter((layer) => isSurveyLayerEnabledInCountry(layer, countryCode)),
    [countryCode]
  );

  const isLayerSelected = useCallback(
    (layerType) => {
      if (layerType === selectedLayer) {
        return true;
      }

      if (enableMultipleSelect) {
        return multipleSelections.includes(layerType);
      } else {
        return layerType === selectedLayer;
      }
    },
    [selectedLayer, enableMultipleSelect, multipleSelections]
  );

  const handleOnClick = useCallback(
    (layer) => {
      if (layer !== selectedLayer) {
        onLayerChanged(layer);
      }
    },
    [selectedLayer, onLayerChanged]
  );

  const handleOnCheckboxClick = useCallback(
    (layer) => {
      if (enableMultipleSelect) {
        let layers = [...multipleSelections];

        if (layers.includes(layer)) {
          layers = layers.filter((item) => item !== layer);
        } else {
          layers.push(layer);
        }

        onReferencesChanged(layers);
      }
    },
    [selectedLayer, onLayerChanged, multipleSelections]
  );

  const getColorStrategy = (layer) => {
    const colorStrategy = new DefaultColoringStrategy(layer);
    return colorStrategy;
  };

  const renderListItem =
    (enabled = false) =>
    (layer) => {
      if (useCanLayerBeEnabled(layer) === true) {
        const config = getLayerConfig(layer);
        return (
          <Box
            key={layer}
            sx={{
              my: 0,
            }}
          >
            <Paper elevation={0}>
              <Box
                sx={{
                  borderLeft: '4px solid ' + getColorStrategy(layer).getColor(config.maxValue),
                }}
              >
                <MapLegendLayerListItem
                  layer={layer}
                  disabled={!enabled || disableAll}
                  enableMultipleSelect={enableMultipleSelect && layer !== selectedLayer}
                  isSelected={isLayerSelected(layer)}
                  onCheckboxClick={handleOnCheckboxClick}
                  onClick={handleOnClick}
                  color
                  renderListItemSecondayAction={() => (
                    <ListItemSecondaryAction style={{ pointerEvents: 'none' }}>
                      <Box
                        sx={{
                          color: !enabled || disableAll ? grey['400'] : 'black',
                          fontSize: '0.8rem',
                        }}
                      >
                        {config.getShorthand(LangFile)}
                      </Box>
                    </ListItemSecondaryAction>
                  )}
                />
              </Box>
            </Paper>
          </Box>
        );
      }
      return null;
    };

  const renderEmptyState = useCallback(() => {
    if (isSingleField) {
      return LangFileRef.current.SurveyLayerList.noLayersFound;
    } else {
      return (
        <>
          <span>{LangFileRef.current.SurveyLayerList.noneApplicable}</span>
          <span>{LangFileRef.current.SurveyLayerList.tryAnalysing}</span>
        </>
      );
    }
  }, [isSingleField]);

  return (
    <List dense disablePadding>
      <SubList
        title={LangFile.SurveyLayerList.nutrients}
        items={nutrientLayers}
        renderEmptyState={renderEmptyState}
        renderItem={renderListItem(true)}
      />

      <SubList
        title={LangFile.SurveyLayerList.texture}
        items={textureLayers}
        renderEmptyState={renderEmptyState}
        renderItem={renderListItem(true)}
      />

      <SubList
        title={LangFile.SurveyLayerList.topography}
        items={topographyLayers}
        renderEmptyState={renderEmptyState}
        renderItem={renderListItem(viewMode !== ViewModeConstants.OVERVIEW)}
      />
    </List>
  );
};

SurveyLayerList.propTypes = {
  selectedLayer: PropTypes.string,
  onLayerChanged: PropTypes.func,
  onReferencesChanged: PropTypes.func,
  isSingleField: PropTypes.bool,
  disableAll: PropTypes.bool,
  enableMultipleSelect: PropTypes.bool,
  multipleSelections: PropTypes.array,
};

SurveyLayerList.defaultProps = {
  onLayerChanged: voidFunc,
  onReferencesChanged: voidFunc,
};

export default memo(SurveyLayerList);
